import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { toast } from 'react-toastify';
import { mockApi } from '../../mock/api';
import { PageHeader, StatusBadge } from '../../components/ui';
import { formatDateTime } from '../../utils';
import type { CmsPage, PublishStatus } from '../../types';

const CmsPageEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState<CmsPage | null>(null);
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState<PublishStatus>('Draft');
  const [content, setContent] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await mockApi.getCmsPage(id);
      setPage(data);
      setTitle(data.title);
      setStatus(data.status);
      setContent(data.content);
    } catch {
      toast.error('Page not found');
      navigate('/cms');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!page || !title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const updated = await mockApi.saveCmsPage(page.id, {
        title: title.trim(),
        status,
        content,
      });
      setPage(updated);
      toast.success('Page saved');
    } catch {
      toast.error('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !page) {
    return (
      <Box>
        <PageHeader title="Edit Page" />
        <Typography color="text.secondary">Loading…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={page.title}
        subtitle={`/${page.slug}`}
        breadcrumbs={[
          { label: 'CMS', to: '/cms' },
          { label: page.title },
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              component={RouterLink}
              to="/cms"
              startIcon={<ArrowBackIcon />}
              variant="outlined"
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveOutlinedIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              Save
            </Button>
          </Stack>
        }
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <TextField
                fullWidth
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Content (rich text editor placeholder)
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={14}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write page content…"
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontSize: 15,
                    lineHeight: 1.7,
                    bgcolor: 'background.default',
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h3">Publishing</Typography>
                <StatusBadge status={status} />
              </Box>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as PublishStatus)}
                >
                  <MenuItem value="Draft">Draft</MenuItem>
                  <MenuItem value="Published">Published</MenuItem>
                  <MenuItem value="Unpublished">Unpublished</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                Last updated
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatDateTime(page.updatedAt)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CmsPageEdit;
